import React, { useState, useEffect } from 'react'
import './daily-generation-report.scss'
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  TableSortLabel,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  FileUpload as FileUploadIcon,
  CalendarToday as CalendarTodayIcon,
  Business as BusinessIcon,
} from '@mui/icons-material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import DGRForm from './components/DGRForm/DGRForm'
import DGRDetail from './components/DGRDetail/DGRDetail'
import ImportDGRModal from './components/ImportDGRModal/ImportDGRModal'
import {
  fetchDGRReports,
  createDGRReport,
  updateDGRReport,
  deleteDGRReport,
  fetchPlantListForDGR,
  fetchPlantMetrics,
} from './services/dgrService'
import SolarPlantIcon from '../../assets/images/solar plant_3.svg'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'

const DailyGenerationReport = () => {
  // State management
  const [reports, setReports] = useState([])
  const [filteredReports, setFilteredReports] = useState([])
  const [loading, setLoading] = useState(true)
  const [plantLoading, setPlantLoading] = useState(false)
  const [selectedReport, setSelectedReport] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [showDetail, setShowDetail] = useState(false)
  const [reportToDelete, setReportToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedPlant, setSelectedPlant] = useState(null)
  const [plants, setPlants] = useState([])
  const [selectedDate, setSelectedDate] = useState(dayjs())
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [importStatus, setImportStatus] = useState({
    open: false,
    message: '',
    severity: 'success',
    errors: [],
  })


  const [filterMode, setFilterMode] = useState('date') //toggle date or plant
  
  // New: sort state for date column ('asc' or 'desc')
  const [dateSortDirection, setDateSortDirection] = useState('desc')

  // For plant mode: date range
  const [startDate, setStartDate] = useState(dayjs().subtract(7, 'days'))
  const [endDate, setEndDate] = useState(dayjs())

  // Load reports on component mount
  useEffect(() => {
    loadReports()
    loadPlants()
  }, [])

  // Filter reports when filters change
  useEffect(() => {
    filterReports()
  }, [reports, searchTerm, selectedPlant, selectedDate, filterMode, startDate, endDate])

  const loadPlants = async () => {
    try {
      setPlantLoading(true)
      const plantList = await fetchPlantListForDGR()
      setPlants(plantList)
    } catch (error) {
      console.error('Error loading plants:', error)
      setPlants([])
    } finally {
      setPlantLoading(false)
    }
  }

  const loadReports = async () => {
    try {
      setLoading(true)
      // Load all reports since we're filtering by date/plant on frontend
      const data = await fetchDGRReports({})
      console.log('Reports loaded:', data.length, 'reports')
      setReports(data)
    } catch (error) {
      console.error('Error loading reports:', error)
      setReports([])
    } finally {
      setLoading(false)
    }
  }

  const filterReports = () => {
    let filtered = [...reports]

    if (filterMode === 'date') {
      //Date mode: filter by exact date match
      if (selectedDate) {
        filtered = filtered.filter((report) => {
          try {
            const reportDate = dayjs(report.date || report.reportDate)
            const filterDate = dayjs(selectedDate)
            
            if (!reportDate.isValid() || !filterDate.isValid()) {
              return false
            }
            
            return reportDate.isSame(filterDate, 'day')
          } catch (error) {
            console.error('Date filtering error:', error)
            return false
          }
        })
      }

      //additional plant filter in date mode
      if (selectedPlant) {
        filtered = filtered.filter((report) => report.plantId === selectedPlant._id)
      }
    } else if (filterMode === 'plant') {
      //Plant mode: filter by plant and date range
      if (selectedPlant) {
        filtered = filtered.filter((report) => report.plantId === selectedPlant._id)
      }

      //Date range filter 
      if (startDate && endDate) {
        filtered = filtered.filter((report) => {
          try {
            const reportDate = dayjs(report.date || report.reportDate)
            const start = dayjs(startDate)
            const end = dayjs(endDate)
            
            if (!reportDate.isValid()) {
              return false
            }
            
            return (reportDate.isSame(start, 'day') || reportDate.isAfter(start, 'day')) && 
                   (reportDate.isSame(end, 'day') || reportDate.isBefore(end, 'day'))
          } catch (error) {
            console.error('Date range filtering error:', error)
            return false
          }
        })
      }
    }

    setFilteredReports(filtered)
  }

  // Toggle sort direction when user clicks Date header
  const toggleDateSort = () => {
    setDateSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
  }

  // Compute sorted reports from filteredReports before rendering
  const sortedReports = React.useMemo(() => {
    const arr = [...filteredReports]
    const compareDates = (a, b) => {
      try {
        const da = dayjs(a.date || a.reportDate)
        const db = dayjs(b.date || b.reportDate)
        const va = da.isValid() ? da.valueOf() : null
        const vb = db.isValid() ? db.valueOf() : null
        // Place invalid dates at the end
        if (va === null && vb === null) return 0
        if (va === null) return 1
        if (vb === null) return -1
        return dateSortDirection === 'asc' ? va - vb : vb - va
      } catch (err) {
        // fallback: keep original order on error
        return 0
      }
    }
    arr.sort(compareDates)
    return arr
  }, [filteredReports, dateSortDirection])

  const handleCreateReport = () => {
    setSelectedReport(null)
    setEditMode(false)
    setShowForm(true)
  }

  const handleEditReport = (report) => {
    setSelectedReport({
      ...report,
      id: report.id?.$oid || report.id || null,
    })
    setEditMode(true)
    setShowForm(true)
  }

  const handleViewReport = (report) => {
    setSelectedReport({
      ...report,
      id: report.id?.$oid || report.id || null,
    })
    setShowDetail(true)
  }

  const handleDeleteReport = async (report) => {
    const reportId = report.id?.$oid || report.id
    if (!reportId) {
      console.error('No valid ID found for report:', report)
      return
    }
    setReportToDelete({ ...report, id: reportId })
    setShowDeleteModal(true)
  }

  const confirmDeleteReport = async () => {
    if (!reportToDelete) return

    try {
      await deleteDGRReport(reportToDelete.id)
      // Optimistic update: Remove from local state instead of reloading
      setReports((prev) => prev.filter((r) => r.id !== reportToDelete.id))
    } catch (error) {
      console.error('Error deleting report', error)
      // Reload on error to sync with server
      await loadReports()
    } finally {
      setShowDeleteModal(false)
      setReportToDelete(null)
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteModal(false)
    setReportToDelete(null)
  }

  const handleFormSubmit = async (reportData) => {
    try {
      if (editMode && selectedReport) {
        const updatedReport = await updateDGRReport(selectedReport.id, reportData)
        // Optimistic update: Update the report in the table
        setReports((prev) =>
          prev.map((r) =>
            r.id === selectedReport.id
              ? { ...updatedReport, id: updatedReport.id?.$oid || updatedReport.id }
              : r
          )
        )
      } else {
        const newReport = await createDGRReport(reportData)
        // Optimistic update: Add new report to the beginning of the list
        setReports((prev) => [
          { ...newReport, id: newReport.id?.$oid || newReport.id },
          ...prev,
        ])
      }
      setShowForm(false)
      setSelectedReport(null)
    } catch (error) {
      console.error('Error saving report:', error)
      // Reload on error to sync with server
      await loadReports()
    }
  }

  const handleFormClose = () => {
    setShowForm(false)
    setSelectedReport(null)
    setEditMode(false)
  }

  const handleDetailClose = () => {
    setShowDetail(false)
    setSelectedReport(null)
  }

  const handleOpenImportModal = () => {
    setImportModalOpen(true)
  }

  const handleCloseImportModal = () => {
    setImportModalOpen(false)
  }

  const handleImportSuccess = async (importResult) => {
    try {
      const created = importResult?.created || 0
      const skipped = importResult?.skipped || 0
      const errors = importResult?.errors || []
      const total = created + skipped

      const message = `Imported ${created} out of ${total} reports.`
      const severity = errors && errors.length > 0 ? 'error' : skipped > 0 ? 'warning' : 'success'

      setImportStatus({ open: true, message, severity, errors })
    } catch (err) {
      console.error('Error preparing import notification:', err)
    }

    //Refresh the reports list after successful import
    await loadReports()
  }

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <div className="daily-generation-report">
      <div className="dgr-header">
        <Typography variant="h4" component="h1" className="page-title">
          Daily Generation Report (DGR)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<FileUploadIcon />}
            onClick={handleOpenImportModal}
            className="import-dgr-btn"
            sx={{
              backgroundColor: '#5968b7',
              '&:hover': {
                backgroundColor: '#4a5a9e',
              },
            }}
          >
            Import
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateReport}
            className="create-dgr-btn"
          >
            New Entry
          </Button>
        </Box>
      </div>

      {/* Filters Section */}
      <Card className="filters-card" sx={{ mt: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Autocomplete
                size="small"
                options={plants}
                getOptionLabel={(option) => option.plantName || ''}
                value={selectedPlant}
                onChange={(event, newValue) => {
                  setSelectedPlant(newValue)
                }}
                loading={plantLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Plant Name"
                    placeholder="Select a plant..."
                    sx={{ minWidth: 300 }}
                  />
                )}
                sx={{ minWidth: 300 }}
              />      
              {filterMode === 'date' ? (
                //Single date picker for Date mode
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Report Date"
                    format="DD-MM-YYYY"
                    value={selectedDate}
                    onChange={(date) => setSelectedDate(date)}
                    slotProps={{
                      textField: { size: 'small', sx: { minWidth: 160 } },
                    }}
                  />
                </LocalizationProvider>
              ) : (
                //Date range pickers for Plant mode
                <>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="From Date"
                      format="DD-MM-YYYY"
                      value={startDate}
                      onChange={(date) => setStartDate(date)}
                      maxDate={endDate}
                      slotProps={{
                        textField: { size: 'small', sx: { minWidth: 160 } },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="To Date"
                      format="DD-MM-YYYY"
                      value={endDate}
                      onChange={(date) => setEndDate(date)}
                      minDate={startDate}
                      slotProps={{
                        textField: { size: 'small', sx: { minWidth: 160 } },
                      }}
                    />
                  </LocalizationProvider>
                </>
              )}
            </Box>
            <Box>
              <ToggleButtonGroup
                value={filterMode}
                exclusive
                onChange={(event, newMode) => {
                  if (newMode !== null) {
                    setFilterMode(newMode)
                  }
                }}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    color: '#5968b7',
                    borderColor: '#5968b7',
                    px: 1.5,
                    py: 0.5,
                    '& img': {
                      width: 18,
                      height: 18,
                      transition: 'filter 150ms ease',
                      display: 'inline-block',
                      verticalAlign: 'middle',
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#5968b7',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#4a5a9e',
                      },
                      '& img': {
                        filter: 'brightness(0) invert(1)'
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(89, 104, 183, 0.1)',
                    },
                  },
                }}
              >
                <ToggleButton value="date" aria-label="date mode">
                  <CalendarTodayIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="plant" aria-label="plant mode">
                  <img
                    src={SolarPlantIcon}
                    alt="plant"
                    style={{ width: 18, height: 18 }}
                  />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Reports Table */}
      <Card className="dgr-table-card" sx={{ mt: 2 }}>
        <CardContent>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Sr. No.</TableCell>
                    <TableCell
                      sortDirection={dateSortDirection}
                      onClick={toggleDateSort}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <TableSortLabel
                        active
                        direction={dateSortDirection}
                      >
                        Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Plant Name</TableCell>
                    <TableCell>Plant Capacity (kWp)</TableCell>
                    <TableCell>Plant Location</TableCell>
                    <TableCell>GTI (W/m²)</TableCell>
                    <TableCell>Act Yield (kWh/kWp)</TableCell>
                    <TableCell>Act Generation (kWh)</TableCell>
                    <TableCell>PR (%)</TableCell>
                    <TableCell>Remarks</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedReports.map((report, idx) => (
                    <TableRow key={report.id} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        {dayjs(report.date || report.reportDate).isValid()
                          ? dayjs(report.date || report.reportDate).format('DD-MM-YYYY')
                          : 'N/A'}
                      </TableCell>
                      <TableCell>{report.plantName || 'N/A'}</TableCell>
                      <TableCell>{report.plantCapacity ?? 'N/A'}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 300,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={report.plantLocation || ''}
                      >
                        {report.plantLocation || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {report.gti !== null && report.gti !== undefined && isFinite(Number(report.gti))
                          ? Number(report.gti).toFixed(2)
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {report.actYield !== null && report.actYield !== undefined && isFinite(Number(report.actYield))
                          ? Number(report.actYield).toFixed(2)
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {report.actGeneration !== null && report.actGeneration !== undefined && isFinite(Number(report.actGeneration))
                          ? Number(report.actGeneration).toFixed(2)
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {report.pr !== null && report.pr !== undefined && isFinite(Number(report.pr))
                          ? Number(report.pr).toFixed(2)
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {report.remarks ? report.remarks.substring(0, 30) + '...' : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleViewReport(report)}
                          title="View Details"
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleEditReport(report)}
                          title="Edit Report"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteReport(report)}
                          title="Delete Report"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {sortedReports.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No reports found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </CardContent>
      </Card>

      {/* DGR Form Dialog */}
      <Dialog
        open={showForm}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleFormClose()
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editMode ? 'Edit Daily Generation Report' : 'Create New Daily Generation Report'}
          <IconButton
            onClick={handleFormClose}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DGRForm
            report={selectedReport}
            onSubmit={handleFormSubmit}
            onCancel={handleFormClose}
          />
        </DialogContent>
      </Dialog>

      {/* DGR Detail Dialog */}
      <Dialog
        open={showDetail}
        onClose={(event, reason) => {
          if (reason === 'backdropClick'|| reason === 'escapeKeyDown') return;
          handleDetailClose()
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Daily Generation Report Details
          <IconButton
            onClick={handleDetailClose}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedReport && (
            <DGRDetail
              report={selectedReport}
              onEdit={() => {
                setShowDetail(false)
                handleEditReport(selectedReport)
              }}
              onClose={handleDetailClose}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete DGR Modal */}
      <Dialog
        open={showDeleteModal}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return;
          handleCancelDelete()
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Delete Daily Generation Report
          <IconButton
            onClick={handleCancelDelete}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>
            You are about to delete the report for{' '}
            <strong>
              {reportToDelete?.plantName} on{' '}
              {dayjs(reportToDelete?.reportDate || reportToDelete?.date).format('DD-MM-YYYY')}
            </strong>
            . Do you want to proceed? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteReport}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import DGR Modal */}
      <ImportDGRModal
        open={importModalOpen}
        onClose={handleCloseImportModal}
        onImportSuccess={handleImportSuccess}
      />

      {/* Import Status Snackbar */}
      <Snackbar
        open={importStatus.open}
        autoHideDuration={10000}
        onClose={() => setImportStatus({ ...importStatus, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setImportStatus({ ...importStatus, open: false })}
          severity={importStatus.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          <div>{importStatus.message}</div>
          {importStatus.errors && importStatus.errors.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {importStatus.errors.slice(0, 5).map((e, i) => (
                <div key={i} style={{ fontSize: 12 }}>
                  {`Row ${e.row} (${e.plantName}, ${e.date}): ${e.reason}`}
                </div>
              ))}
              {importStatus.errors.length > 5 && (
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  ...and {importStatus.errors.length - 5} more error(s)
                </div>
              )}
            </div>
          )}
        </Alert>
      </Snackbar>
    </div>
  )
}

export default DailyGenerationReport
